import { useState, useEffect } from 'react';

const SecuritySettings = () => {
  const [formData, setFormData] = useState({
    authentication: {
      sessionTimeout: 60,
      maxLoginAttempts: 5,
      lockoutDuration: 1,
      passwordPolicy: {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: true,
        passwordExpiration: 90,
        preventReuse: 5,
      },
      twoFactorAuth: {
        enabled: false,
        required: false,
        methods: ['email', 'app'],
      },
    },
    privacy: {
      dataRetention: {
        userAccounts: 365,
        activityLogs: 90,
        paymentInfo: 730,
      },
      cookieConsent: {
        enabled: true,
        expiration: 30,
      },
      privacyPolicy: {
        lastUpdated: '2025-01-15',
        version: '1.2',
        url: '/privacy-policy',
      },
      termsOfService: {
        lastUpdated: '2025-01-15',
        version: '1.2',
        url: '/terms-of-service',
      },
    },
    security: {
      cors: {
        allowedOrigins: 'https://prokidsapp.com,https://admin.prokidsapp.com',
        allowCredentials: true,
      },
      rateLimit: {
        enabled: true,
        maxRequests: 100,
        timeWindow: 60,
      },
      ssl: {
        enforceHttps: true,
        hsts: true,
      },
      contentSecurity: {
        enabled: true,
        policy:
          "default-src 'self'; script-src 'self' https://cdn.example.com; style-src 'self' https://fonts.googleapis.com; img-src 'self' data: https://cdn.example.com; font-src 'self' https://fonts.gstatic.com;",
      },
    },
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // En una implementación real, obtendríamos estos datos de la API
    // Por ahora, usamos los datos de ejemplo definidos en el estado inicial
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: type === 'checkbox' ? checked : value,
          },
        });
      } else if (parts.length === 3) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: {
              ...formData[parts[0]][parts[1]],
              [parts[2]]: type === 'checkbox' ? checked : value,
            },
          },
        });
      } else if (parts.length === 4) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: {
              ...formData[parts[0]][parts[1]],
              [parts[2]]: {
                ...formData[parts[0]][parts[1]][parts[2]],
                [parts[3]]: type === 'checkbox' ? checked : value,
              },
            },
          },
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleMultiSelectChange = (e) => {
    const { name, options } = e.target;
    const selectedValues = Array.from(options)
      .filter((option) => option.selected)
      .map((option) => option.value);

    const parts = name.split('.');
    setFormData({
      ...formData,
      [parts[0]]: {
        ...formData[parts[0]],
        [parts[1]]: {
          ...formData[parts[0]][parts[1]],
          [parts[2]]: selectedValues,
        },
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      // En una implementación real, enviaríamos estos datos a la API
      console.log('Datos del formulario:', formData);

      // Simulamos una respuesta exitosa
      setTimeout(() => {
        setSuccess(true);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Error al guardar la configuración');
      setLoading(false);
    }
  };

  if (loading && Object.keys(formData).length === 0) {
    return <div className="loading">Cargando configuración...</div>;
  }

  return (
    <div className="settings-section">
      <h2>Configuración de Seguridad</h2>
      <p className="settings-description">
        Configura los ajustes de seguridad y privacidad de la plataforma.
      </p>

      {success && <div className="alert alert-success">Configuración guardada correctamente.</div>}

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="settings-card">
          <h3>Autenticación</h3>

          <div className="form-group">
            <label htmlFor="authentication.sessionTimeout">
              Tiempo de expiración de sesión (minutos)
            </label>
            <input
              type="number"
              id="authentication.sessionTimeout"
              name="authentication.sessionTimeout"
              value={formData.authentication.sessionTimeout}
              onChange={handleInputChange}
              className="form-control"
              min="5"
              required
            />
            <small className="form-text">
              Tiempo de inactividad antes de cerrar la sesión automáticamente
            </small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="authentication.maxLoginAttempts">
                Intentos máximos de inicio de sesión
              </label>
              <input
                type="number"
                id="authentication.maxLoginAttempts"
                name="authentication.maxLoginAttempts"
                value={formData.authentication.maxLoginAttempts}
                onChange={handleInputChange}
                className="form-control"
                min="1"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="authentication.lockoutDuration">Duración del bloqueo (minutos)</label>
              <input
                type="number"
                id="authentication.lockoutDuration"
                name="authentication.lockoutDuration"
                value={formData.authentication.lockoutDuration}
                onChange={handleInputChange}
                className="form-control"
                min="1"
                required
              />
            </div>
          </div>

          <h4>Política de contraseñas</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="authentication.passwordPolicy.minLength">Longitud mínima</label>
              <input
                type="number"
                id="authentication.passwordPolicy.minLength"
                name="authentication.passwordPolicy.minLength"
                value={formData.authentication.passwordPolicy.minLength}
                onChange={handleInputChange}
                className="form-control"
                min="6"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="authentication.passwordPolicy.passwordExpiration">
                Caducidad (días)
              </label>
              <input
                type="number"
                id="authentication.passwordPolicy.passwordExpiration"
                name="authentication.passwordPolicy.passwordExpiration"
                value={formData.authentication.passwordPolicy.passwordExpiration}
                onChange={handleInputChange}
                className="form-control"
                min="0"
                required
              />
              <small className="form-text">0 = sin caducidad</small>
            </div>
            <div className="form-group">
              <label htmlFor="authentication.passwordPolicy.preventReuse">
                Prevenir reutilización
              </label>
              <input
                type="number"
                id="authentication.passwordPolicy.preventReuse"
                name="authentication.passwordPolicy.preventReuse"
                value={formData.authentication.passwordPolicy.preventReuse}
                onChange={handleInputChange}
                className="form-control"
                min="0"
                required
              />
              <small className="form-text">
                Número de contraseñas anteriores que no se pueden reutilizar
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="authentication.passwordPolicy.requireUppercase"
                name="authentication.passwordPolicy.requireUppercase"
                checked={formData.authentication.passwordPolicy.requireUppercase}
                onChange={handleInputChange}
              />
              <label htmlFor="authentication.passwordPolicy.requireUppercase">
                Requerir mayúsculas
              </label>
            </div>
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="authentication.passwordPolicy.requireLowercase"
                name="authentication.passwordPolicy.requireLowercase"
                checked={formData.authentication.passwordPolicy.requireLowercase}
                onChange={handleInputChange}
              />
              <label htmlFor="authentication.passwordPolicy.requireLowercase">
                Requerir minúsculas
              </label>
            </div>
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="authentication.passwordPolicy.requireNumbers"
                name="authentication.passwordPolicy.requireNumbers"
                checked={formData.authentication.passwordPolicy.requireNumbers}
                onChange={handleInputChange}
              />
              <label htmlFor="authentication.passwordPolicy.requireNumbers">Requerir números</label>
            </div>
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="authentication.passwordPolicy.requireSpecialChars"
                name="authentication.passwordPolicy.requireSpecialChars"
                checked={formData.authentication.passwordPolicy.requireSpecialChars}
                onChange={handleInputChange}
              />
              <label htmlFor="authentication.passwordPolicy.requireSpecialChars">
                Requerir caracteres especiales
              </label>
            </div>
          </div>

          <h4>Autenticación de dos factores</h4>
          <div className="form-row">
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="authentication.twoFactorAuth.enabled"
                name="authentication.twoFactorAuth.enabled"
                checked={formData.authentication.twoFactorAuth.enabled}
                onChange={handleInputChange}
              />
              <label htmlFor="authentication.twoFactorAuth.enabled">
                Habilitar autenticación de dos factores
              </label>
            </div>
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="authentication.twoFactorAuth.required"
                name="authentication.twoFactorAuth.required"
                checked={formData.authentication.twoFactorAuth.required}
                onChange={handleInputChange}
                disabled={!formData.authentication.twoFactorAuth.enabled}
              />
              <label htmlFor="authentication.twoFactorAuth.required">
                Requerir para todos los usuarios
              </label>
            </div>
          </div>

          {formData.authentication.twoFactorAuth.enabled && (
            <div className="form-group">
              <label htmlFor="authentication.twoFactorAuth.methods">Métodos permitidos</label>
              <select
                id="authentication.twoFactorAuth.methods"
                name="authentication.twoFactorAuth.methods"
                multiple
                value={formData.authentication.twoFactorAuth.methods}
                onChange={handleMultiSelectChange}
                className="form-control"
                size="3"
                required
              >
                <option value="email">Correo electrónico</option>
                <option value="sms">SMS</option>
                <option value="app">Aplicación de autenticación</option>
              </select>
              <small className="form-text">
                Mantén presionada la tecla Ctrl para seleccionar múltiples opciones
              </small>
            </div>
          )}
        </div>

        <div className="settings-card">
          <h3>Privacidad</h3>

          <h4>Retención de datos</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="privacy.dataRetention.userAccounts">Cuentas de usuario (días)</label>
              <input
                type="number"
                id="privacy.dataRetention.userAccounts"
                name="privacy.dataRetention.userAccounts"
                value={formData.privacy.dataRetention.userAccounts}
                onChange={handleInputChange}
                className="form-control"
                min="30"
                required
              />
              <small className="form-text">
                Tiempo de retención después de la eliminación de la cuenta
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="privacy.dataRetention.activityLogs">
                Registros de actividad (días)
              </label>
              <input
                type="number"
                id="privacy.dataRetention.activityLogs"
                name="privacy.dataRetention.activityLogs"
                value={formData.privacy.dataRetention.activityLogs}
                onChange={handleInputChange}
                className="form-control"
                min="30"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="privacy.dataRetention.paymentInfo">Información de pago (días)</label>
              <input
                type="number"
                id="privacy.dataRetention.paymentInfo"
                name="privacy.dataRetention.paymentInfo"
                value={formData.privacy.dataRetention.paymentInfo}
                onChange={handleInputChange}
                className="form-control"
                min="30"
                required
              />
            </div>
          </div>

          <h4>Consentimiento de cookies</h4>
          <div className="form-row">
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="privacy.cookieConsent.enabled"
                name="privacy.cookieConsent.enabled"
                checked={formData.privacy.cookieConsent.enabled}
                onChange={handleInputChange}
              />
              <label htmlFor="privacy.cookieConsent.enabled">
                Habilitar banner de consentimiento de cookies
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="privacy.cookieConsent.expiration">
                Expiración del consentimiento (días)
              </label>
              <input
                type="number"
                id="privacy.cookieConsent.expiration"
                name="privacy.cookieConsent.expiration"
                value={formData.privacy.cookieConsent.expiration}
                onChange={handleInputChange}
                className="form-control"
                min="1"
                required={formData.privacy.cookieConsent.enabled}
              />
            </div>
          </div>

          <h4>Documentos legales</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="privacy.privacyPolicy.url">URL de la política de privacidad</label>
              <input
                type="text"
                id="privacy.privacyPolicy.url"
                name="privacy.privacyPolicy.url"
                value={formData.privacy.privacyPolicy.url}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="privacy.privacyPolicy.version">Versión</label>
              <input
                type="text"
                id="privacy.privacyPolicy.version"
                name="privacy.privacyPolicy.version"
                value={formData.privacy.privacyPolicy.version}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="privacy.privacyPolicy.lastUpdated">Última actualización</label>
              <input
                type="date"
                id="privacy.privacyPolicy.lastUpdated"
                name="privacy.privacyPolicy.lastUpdated"
                value={formData.privacy.privacyPolicy.lastUpdated}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="privacy.termsOfService.url">URL de los términos de servicio</label>
              <input
                type="text"
                id="privacy.termsOfService.url"
                name="privacy.termsOfService.url"
                value={formData.privacy.termsOfService.url}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="privacy.termsOfService.version">Versión</label>
              <input
                type="text"
                id="privacy.termsOfService.version"
                name="privacy.termsOfService.version"
                value={formData.privacy.termsOfService.version}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="privacy.termsOfService.lastUpdated">Última actualización</label>
              <input
                type="date"
                id="privacy.termsOfService.lastUpdated"
                name="privacy.termsOfService.lastUpdated"
                value={formData.privacy.termsOfService.lastUpdated}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3>Seguridad de la API</h3>

          <div className="form-group">
            <label htmlFor="security.cors.allowedOrigins">Orígenes permitidos (CORS)</label>
            <input
              type="text"
              id="security.cors.allowedOrigins"
              name="security.cors.allowedOrigins"
              value={formData.security.cors.allowedOrigins}
              onChange={handleInputChange}
              className="form-control"
              required
            />
            <small className="form-text">
              Lista de dominios separados por comas (ej:
              https://example.com,https://api.example.com)
            </small>
          </div>

          <div className="form-group checkbox">
            <input
              type="checkbox"
              id="security.cors.allowCredentials"
              name="security.cors.allowCredentials"
              checked={formData.security.cors.allowCredentials}
              onChange={handleInputChange}
            />
            <label htmlFor="security.cors.allowCredentials">
              Permitir credenciales en solicitudes CORS
            </label>
          </div>

          <h4>Limitación de tasa</h4>
          <div className="form-row">
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="security.rateLimit.enabled"
                name="security.rateLimit.enabled"
                checked={formData.security.rateLimit.enabled}
                onChange={handleInputChange}
              />
              <label htmlFor="security.rateLimit.enabled">Habilitar limitación de tasa</label>
            </div>
            <div className="form-group">
              <label htmlFor="security.rateLimit.maxRequests">Solicitudes máximas</label>
              <input
                type="number"
                id="security.rateLimit.maxRequests"
                name="security.rateLimit.maxRequests"
                value={formData.security.rateLimit.maxRequests}
                onChange={handleInputChange}
                className="form-control"
                min="1"
                required={formData.security.rateLimit.enabled}
              />
            </div>
            <div className="form-group">
              <label htmlFor="security.rateLimit.timeWindow">Ventana de tiempo (segundos)</label>
              <input
                type="number"
                id="security.rateLimit.timeWindow"
                name="security.rateLimit.timeWindow"
                value={formData.security.rateLimit.timeWindow}
                onChange={handleInputChange}
                className="form-control"
                min="1"
                required={formData.security.rateLimit.enabled}
              />
            </div>
          </div>

          <h4>SSL/TLS</h4>
          <div className="form-row">
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="security.ssl.enforceHttps"
                name="security.ssl.enforceHttps"
                checked={formData.security.ssl.enforceHttps}
                onChange={handleInputChange}
              />
              <label htmlFor="security.ssl.enforceHttps">Forzar HTTPS</label>
            </div>
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="security.ssl.hsts"
                name="security.ssl.hsts"
                checked={formData.security.ssl.hsts}
                onChange={handleInputChange}
              />
              <label htmlFor="security.ssl.hsts">
                Habilitar HSTS (HTTP Strict Transport Security)
              </label>
            </div>
          </div>

          <h4>Política de seguridad de contenido (CSP)</h4>
          <div className="form-group checkbox">
            <input
              type="checkbox"
              id="security.contentSecurity.enabled"
              name="security.contentSecurity.enabled"
              checked={formData.security.contentSecurity.enabled}
              onChange={handleInputChange}
            />
            <label htmlFor="security.contentSecurity.enabled">Habilitar CSP</label>
          </div>

          {formData.security.contentSecurity.enabled && (
            <div className="form-group">
              <label htmlFor="security.contentSecurity.policy">Política CSP</label>
              <textarea
                id="security.contentSecurity.policy"
                name="security.contentSecurity.policy"
                value={formData.security.contentSecurity.policy}
                onChange={handleInputChange}
                className="form-control"
                rows="4"
                required={formData.security.contentSecurity.enabled}
              ></textarea>
              <small className="form-text">
                Directivas de la política de seguridad de contenido
              </small>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SecuritySettings;
